/**
 * Simple template engine para inyectar valores en HTML
 * Soporta:
 * - {{VARIABLE}} - variables simples
 * - {{#ARRAY}}...{{/ARRAY}} - loops
 * - {{#VARIABLE}}...{{/VARIABLE}} - condicionales
 */

interface TemplateData {
  [key: string]: string | number | boolean | any[] | Record<string, any>;
}

export class TemplateEngine {
  /**
   * Carga un archivo HTML desde assets y lo renderiza con los datos
   */
  static async loadAndRender(templatePath: string, data: TemplateData): Promise<string> {
    const templateUrl = `${templatePath}?v=${Date.now()}`;
    const response = await fetch(templateUrl, {
      cache: 'no-store',
    });
    if (!response.ok) {
      throw new Error(`Failed to load template: ${templatePath}`);
    }
    const html = await response.text();
    return this.render(html, data);
  }

  /**
   * Renderiza un string HTML inyectando variables y loops
   */
  static render(template: string, data: TemplateData): string {
    let result = template;

    // 1. Procesar loops {{#ARRAY}}...{{/ARRAY}}
    result = this.processLoops(result, data);

    // 2. Procesar condicionales {{#VAR}}...{{/VAR}}
    result = this.processConditionals(result, data);

    // 3. Reemplazar variables simples {{VAR}}
    result = this.processVariables(result, data);

    return result;
  }

  /**
   * Procesa loops: {{#ARRAY}}...{{/ARRAY}}
   * Repite el contenido una vez para cada elemento del array
   */
  private static processLoops(template: string, data: TemplateData): string {
    // Regex: {{#nombreArray}}contenido{{/nombreArray}}
    const loopRegex = /\{\{\s*#\s*(\w+)\s*\}\}([\s\S]*?)\{\{\s*\/\s*\1\s*\}\}/g;

    return template.replace(loopRegex, (fullMatch, arrayName, content) => {
      const arrayData = data[arrayName];

      // Si no es un array o no existe, no procesar
      if (!Array.isArray(arrayData)) {
        return fullMatch; // Dejar sin cambios para processConditionals
      }

      // Repetir el contenido para cada elemento
      return arrayData
        .map((item) => {
          // Para cada elemento, interpolar sus propiedades en el contenido
          if (typeof item === 'object' && item !== null) {
            return this.interpolateObject(content, item);
          }
          return content;
        })
        .join('');
    });
  }

  /**
   * Reemplaza {{VARIABLE}} dentro de un string usando propiedades de un objeto
   * También procesa condicionales anidadas con el contexto del objeto
   */
  private static interpolateObject(template: string, obj: any): string {
    let result = template;

    // Primero procesar condicionales anidadas con el contexto del objeto
    result = result.replace(
      /\{\{\s*#\s*(\w+)\s*\}\}([\s\S]*?)\{\{\s*\/\s*\1\s*\}\}/g,
      (fullMatch, varName, content) => {
        const value = obj[varName];
        // Si es falsy, eliminar el bloque; si es truthy, mantener el contenido
        return value ? content : '';
      },
    );

    // Luego reemplazar variables simples
    const regex = /\{\{\s*(\w+)\s*\}\}/g;
    result = result.replace(regex, (match, key) => {
      const value = obj[key];
      if (value === undefined || value === null) {
        return '';
      }
      return String(value);
    });

    return result;
  }

  /**
   * Procesa condicionales: {{#VARIABLE}}...{{/VARIABLE}}
   * Muestra el contenido si VARIABLE es truthy
   */
  private static processConditionals(template: string, data: TemplateData): string {
    const conditionalRegex = /\{\{\s*#\s*(\w+)\s*\}\}([\s\S]*?)\{\{\s*\/\s*\1\s*\}\}/g;

    return template.replace(conditionalRegex, (fullMatch, varName, content) => {
      const value = data[varName];

      // Si es falsy, eliminar el bloque
      if (!value) {
        return '';
      }

      // Si es un array, NO procesar aquí (ya fue en loops)
      if (Array.isArray(value)) {
        return fullMatch;
      }

      // Si es truthy (string, número, objeto), mostrar contenido
      return content;
    });
  }

  /**
   * Reemplaza {{VARIABLE}} con valores del contexto global
   */
  private static processVariables(template: string, data: TemplateData): string {
    const regex = /\{\{\s*(\w+)\s*\}\}/g;

    return template.replace(regex, (match, varName) => {
      const value = data[varName];

      // Si no existe o es undefined/null, devolver vacío (sin {{VAR}})
      if (value === undefined || value === null) {
        return '';
      }

      // Si es un array u objeto, no intentar stringificar (ya fue procesado)
      if (Array.isArray(value) || typeof value === 'object') {
        return '';
      }

      // Convertir a string
      return String(value);
    });
  }
}
